import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CircularProgress from "@mui/material/CircularProgress";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { readApiErrorMessage } from "../../../pages/Postulaciones/postulacionesUtils";
import { esDocumentoRotable, normalizarGiro, rotarDocumento, versionArchivo as versionGuardada } from "./rotarDocumento";

/**
 * Documento del modelo nuevo (job-application-service) tal como lo devuelven
 * `application/detail` y `application/person-documents`.
 */
export interface IDocumentoVisor {
  personDocumentId: string;
  documentType: string;
  originalFileName: string;
  mimeType: string;
  extension: string;
  size: number;
}

export const MODAL_FONT = "'Inter', 'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const formatBytes = (bytes: number) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const VIEWER_HEIGHT = 440;

const toolButtonStyle = {
  width: "26px", height: "26px", padding: 0, borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer",
  fontFamily: MODAL_FONT, fontSize: "15px", color: "#475569", display: "flex", alignItems: "center", justifyContent: "center",
} as const;

// Botones de rotar: sin fondo ni borde (los íconos de giro son asimétricos y
// dentro de una caja se ven descentrados) y centrado explícito del ícono.
const rotateButtonStyle = {
  width: "28px", height: "28px", minWidth: "28px", padding: 0, margin: 0, boxSizing: "border-box", borderRadius: "6px",
  border: "none", background: "transparent", cursor: "pointer", color: "#0065DD", lineHeight: 0,
  display: "inline-grid", placeItems: "center",
} as const;

const separatorStyle = { width: "1px", height: "18px", background: "#cbd5e1", margin: "0 4px" } as const;

/**
 * Una fila de documento con su visor inline (imagen con zoom / PDF con páginas),
 * rotación izquierda/derecha, botón de descarga (vía el proxy del backend, sin
 * exponer el SAS) y "Abrir en nueva pestaña".
 *
 * Apertura: por defecto cada fila se abre y cierra sola; con `open` + `onToggle`
 * el padre la controla (p. ej. para que haya un solo visor abierto a la vez).
 *
 * Rotación: el giro se previsualiza al instante y se GUARDA sobrescribiendo el
 * archivo (ver rotarDocumento). Dos modos:
 *  - Inmediato (por defecto): aparece "Guardar rotación" en la barra del visor.
 *  - Diferido (`rotacionPendiente` + `onRotacionPendienteChange`): el padre guarda
 *    el giro junto con su propio botón de guardar (p. ej. la validación manual).
 *
 * `title`/`subtitle` sustituyen el nombre del archivo y la línea de metadatos;
 * `extraActions` inyecta botones extra (Reemplazar/Quitar).
 *
 * `variante="lista"`: fila plana para listas dentro de una tarjeta (ícono del
 * tipo de archivo, título y metadatos en una línea, botones con ícono). Las
 * filas se separan con una línea; el visor se abre igual debajo.
 */
const DocumentoConVisor = ({
  doc,
  title,
  subtitle,
  extraActions,
  open: openProp,
  onToggle,
  rotacionPendiente,
  onRotacionPendienteChange,
  onRotado,
  versionArchivo = 0,
  permitirRotar = true,
  variante = "tarjeta",
}: {
  doc: IDocumentoVisor;
  title?: string;
  subtitle?: string;
  extraActions?: JSX.Element | null;
  open?: boolean;
  onToggle?: () => void;
  rotacionPendiente?: number;
  onRotacionPendienteChange?: (giro: number) => void;
  onRotado?: (info: { size: number }) => void;
  /** El padre lo incrementa cuando reescribió el archivo (recarga el preview). */
  versionArchivo?: number;
  permitirRotar?: boolean;
  variante?: "tarjeta" | "lista";
}) => {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const toggle = () => (onToggle ? onToggle() : setOpenState((v) => !v));

  const [zoomLevel, setZoomLevel] = useState(1);
  const [pdfPage, setPdfPage] = useState(1);
  const [imgOffset, setImgOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, ox: 0, oy: 0 });

  // Giro acumulado (puede ser -90, 450…) para que la animación siempre gire por
  // el camino corto; al guardar se normaliza a 0/90/180/270.
  const diferido = typeof onRotacionPendienteChange === "function";
  const [giroLocal, setGiroLocal] = useState(0);
  const giro = diferido ? rotacionPendiente ?? 0 : giroLocal;
  const giroNormalizado = normalizarGiro(giro);
  const [guardandoGiro, setGuardandoGiro] = useState(false);
  const [errorGiro, setErrorGiro] = useState<string | null>(null);
  // Versión del archivo: cambia tras guardar una rotación (aquí o en el padre,
  // que avisa con `versionArchivo`) y se agrega a la URL para recargar el preview.
  const [, setVersion] = useState(0);
  const version = versionGuardada(doc.personDocumentId);

  const rotar = (delta: number) => {
    setErrorGiro(null);
    if (diferido) onRotacionPendienteChange!(giro + delta);
    else setGiroLocal((g) => g + delta);
  };

  const guardarGiro = async () => {
    if (guardandoGiro || giroNormalizado === 0) return;
    setGuardandoGiro(true);
    setErrorGiro(null);
    try {
      const { size } = await rotarDocumento(doc, giroNormalizado);
      setGiroLocal(0);
      setVersion((v) => v + 1);
      onRotado?.({ size });
    } catch (err) {
      setErrorGiro((err as Error).message);
    } finally {
      setGuardandoGiro(false);
    }
  };

  // Ancho real del área del visor: con el documento girado de lado, su ancho
  // visual es su alto, así que se limita contra este valor.
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const [viewerWidth, setViewerWidth] = useState(0);
  useLayoutEffect(() => {
    const el = viewerRef.current;
    if (!open || !el) return;
    setViewerWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => setViewerWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  useEffect(() => {
    setZoomLevel(1); setPdfPage(1); setImgOffset({ x: 0, y: 0 }); setDragging(false);
    setPreviewLoading(open);
  }, [open]);
  useEffect(() => { if (version > 0) setPreviewLoading(true); }, [version, versionArchivo]);
  useEffect(() => { if (zoomLevel === 1) setImgOffset({ x: 0, y: 0 }); }, [zoomLevel]);

  // Solo formatos que el navegador muestra: un original no procesable (HEIC,
  // TIFF…) cae en "Previsualización no disponible" con el botón de descarga.
  const isImage    = /^image\/(jpeg|png|gif|webp)$/.test(doc.mimeType ?? "");
  const isPdf      = doc.mimeType === "application/pdf";
  const rotable    = permitirRotar && esDocumentoRotable(doc.mimeType);
  // Sin rotación permitida (p. ej. hay un reemplazo pendiente) se muestra derecho.
  const giroVista  = rotable ? giro : 0;
  const deLado     = rotable && (giroNormalizado === 90 || giroNormalizado === 270);
  const appApiBase = process.env.REACT_APP_API_APPLICANT ?? "";
  const viewerUrl  = `${appApiBase}/application/document-file/${encodeURIComponent(doc.personDocumentId)}?inline=true${version ? `&v=${version}` : ""}`;
  const pdfSrc     = isPdf ? `${viewerUrl}#page=${pdfPage}` : viewerUrl;
  const clampZoom  = (z: number) => Math.min(3, Math.max(0.5, Math.round(z * 10) / 10));
  const anchoVisor = viewerWidth || 800;

  const lista = variante === "lista";
  const esPdf = (doc.mimeType ?? "").includes("pdf") || (doc.extension ?? "").toLowerCase() === "pdf";
  const esImagen = (doc.mimeType ?? "").startsWith("image/");

  const subtituloGiro = rotable && giroNormalizado !== 0
    ? diferido ? ` · Rotado ${giroNormalizado}° (se guarda al validar)` : ` · Rotación sin guardar (${giroNormalizado}°)`
    : "";

  return (
    <div
      className={lista ? "doc-visor-lista" : undefined}
      style={lista
        ? { overflow: "hidden", borderRadius: open ? "10px" : 0, border: open ? "1.5px solid #bfdbfe" : "none", margin: open ? "4px 0" : 0, transition: "border-color 0.15s" }
        : { borderRadius: "10px", overflow: "hidden", border: open ? "1.5px solid #bfdbfe" : "1px solid #e2e8f0", transition: "border-color 0.15s" }}
    >
      {/* ── row ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: lista ? "9px 10px" : "10px 14px", background: open ? "#eff6ff" : lista ? "transparent" : "#f8fafc", transition: "background 0.15s" }}>
        {lista && (
          <span style={{ display: "inline-flex", marginRight: "12px", flexShrink: 0, color: esPdf ? "#dc2626" : "#64748b" }}>
            {esPdf
              ? <PictureAsPdfOutlinedIcon style={{ fontSize: 22 }} />
              : esImagen ? <ImageOutlinedIcon style={{ fontSize: 22 }} /> : <InsertDriveFileOutlinedIcon style={{ fontSize: 22 }} />}
          </span>
        )}
        <button
          onClick={toggle}
          style={lista
            ? { display: "flex", flexDirection: "row", alignItems: "baseline", flexWrap: "wrap", columnGap: "14px", rowGap: "2px", minWidth: 0, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", flex: 1 }
            : { display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0, background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", flex: 1 }}
        >
          <span style={{ fontFamily: MODAL_FONT, fontSize: lista ? "14px" : "13px", fontWeight: lista ? 500 : 600, color: open ? "#0065DD" : lista ? "#0f172a" : "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "100%", textDecoration: open ? "underline" : "none", textUnderlineOffset: "3px" }}>
            {title ?? doc.originalFileName}
          </span>
          <span style={{ fontFamily: MODAL_FONT, fontSize: lista ? "12.5px" : "11px", color: lista ? "#64748b" : "#94a3b8", marginTop: lista ? 0 : "2px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
            {subtitle ?? `${doc.documentType} · ${(doc.extension ?? "").toUpperCase()} · ${formatBytes(doc.size)}`}
            {subtituloGiro && <span style={{ color: "#b45309", fontWeight: 600 }}>{subtituloGiro}</span>}
          </span>
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "12px", flexShrink: 0 }}>
          {extraActions}
          <button
            onClick={async () => {
              if (isDownloading) return;
              const baseUrl = process.env.REACT_APP_API_APPLICANT;
              if (!baseUrl) { window.open(viewerUrl, "_blank"); return; }
              const proxyUrl = `${baseUrl}/application/document-file/${encodeURIComponent(doc.personDocumentId)}`;
              setIsDownloading(true);
              try {
                const res = await fetch(proxyUrl, { cache: "no-store" });
                if (!res.ok) throw new Error(await readApiErrorMessage(res));
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = doc.originalFileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch {
                window.location.href = proxyUrl;
              } finally {
                setIsDownloading(false);
              }
            }}
            disabled={isDownloading}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 12px", borderRadius: "7px", border: "1px solid #e2e8f0", background: isDownloading ? "#f8fafc" : "#fff", color: isDownloading ? "#94a3b8" : "#475569", fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 500, cursor: isDownloading ? "wait" : "pointer", whiteSpace: "nowrap", transition: "all 0.15s", minWidth: "108px", justifyContent: "center" }}
            onMouseEnter={(e) => { if (isDownloading) return; const b = e.currentTarget; b.style.background = "#f1f5f9"; b.style.borderColor = "#cbd5e1"; b.style.color = "#1e293b"; }}
            onMouseLeave={(e) => { if (isDownloading) return; const b = e.currentTarget; b.style.background = "#fff"; b.style.borderColor = "#e2e8f0"; b.style.color = "#475569"; }}
          >
            {isDownloading
              ? (<><CircularProgress size={12} thickness={5} sx={{ color: "#94a3b8" }} />Descargando…</>)
              : lista ? (<><FileDownloadOutlinedIcon style={{ fontSize: 17 }} />Descargar</>) : (<>Descargar ↓</>)}
          </button>
          <button
            onClick={toggle}
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: lista ? "5px 14px 5px 12px" : "5px 14px", borderRadius: "7px", border: open ? "1px solid #93c5fd" : "1px solid #bfdbfe", background: open ? "#dbeafe" : "#eff6ff", color: "#0065DD", fontFamily: MODAL_FONT, fontSize: "12px", fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.15s" }}
            onMouseEnter={(e) => { const b = e.currentTarget; b.style.background = open ? "#bfdbfe" : "#dbeafe"; b.style.borderColor = open ? "#60a5fa" : "#93c5fd"; }}
            onMouseLeave={(e) => { const b = e.currentTarget; b.style.background = open ? "#dbeafe" : "#eff6ff"; b.style.borderColor = open ? "#93c5fd" : "#bfdbfe"; }}
          >
            {lista && !open && <VisibilityOutlinedIcon style={{ fontSize: 17 }} />}
            {open ? "Cerrar" : "Ver documento"}
          </button>
        </div>
      </div>

      {/* ── animated inline viewer ── */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="viewer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "6px", padding: "7px 14px", background: "#f1f5f9", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                {isImage && (
                  <>
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>Zoom</span>
                    <button onClick={() => setZoomLevel((z) => clampZoom(z - 0.1))} style={toolButtonStyle}>−</button>
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#1e293b", fontWeight: 600, minWidth: "40px", textAlign: "center" }}>{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={() => setZoomLevel((z) => clampZoom(z + 0.1))} style={toolButtonStyle}>+</button>
                    <button onClick={() => setZoomLevel(1)} style={{ padding: "3px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", fontFamily: MODAL_FONT, fontSize: "11px", color: "#475569" }}>Restablecer</button>
                  </>
                )}
                {isPdf && (
                  <>
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>Página</span>
                    <button onClick={() => setPdfPage((p) => Math.max(1, p - 1))} disabled={pdfPage <= 1} style={{ ...toolButtonStyle, fontSize: "14px", cursor: pdfPage <= 1 ? "not-allowed" : "pointer", color: pdfPage <= 1 ? "#cbd5e1" : "#475569" }}>‹</button>
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#1e293b", fontWeight: 600, minWidth: "24px", textAlign: "center" }}>{pdfPage}</span>
                    <button onClick={() => setPdfPage((p) => p + 1)} style={{ ...toolButtonStyle, fontSize: "14px" }}>›</button>
                  </>
                )}
                {rotable && (
                  <>
                    <span style={separatorStyle} />
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#64748b", fontWeight: 500 }}>Rotar</span>
                    <button title="Rotar a la izquierda" aria-label="Rotar a la izquierda" disabled={guardandoGiro} onClick={() => rotar(-90)} style={rotateButtonStyle}>
                      <RotateLeftIcon style={{ fontSize: 19, display: "block" }} />
                    </button>
                    <button title="Rotar a la derecha" aria-label="Rotar a la derecha" disabled={guardandoGiro} onClick={() => rotar(90)} style={rotateButtonStyle}>
                      <RotateRightIcon style={{ fontSize: 19, display: "block" }} />
                    </button>
                    {giroNormalizado !== 0 && (
                      <>
                        <button
                          onClick={() => { setErrorGiro(null); if (diferido) onRotacionPendienteChange!(0); else setGiroLocal(0); }}
                          disabled={guardandoGiro}
                          style={{ padding: "3px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", background: "#fff", cursor: guardandoGiro ? "not-allowed" : "pointer", fontFamily: MODAL_FONT, fontSize: "11px", color: "#475569" }}
                        >
                          Deshacer
                        </button>
                        {!diferido && (
                          <button
                            onClick={guardarGiro}
                            disabled={guardandoGiro}
                            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "3px 10px", borderRadius: "6px", border: "none", background: guardandoGiro ? "#93c5fd" : "#0065DD", color: "#fff", cursor: guardandoGiro ? "wait" : "pointer", fontFamily: MODAL_FONT, fontSize: "11px", fontWeight: 600 }}
                          >
                            {guardandoGiro && <CircularProgress size={10} thickness={6} sx={{ color: "#fff" }} />}
                            {guardandoGiro ? "Guardando…" : "Guardar rotación"}
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
              <a href={viewerUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: MODAL_FONT, fontSize: "11px", color: "#0065DD", textDecoration: "none", fontWeight: 500 }}>
                Abrir en nueva pestaña ↗
              </a>
            </div>
            {errorGiro && (
              <div style={{ padding: "6px 14px", background: "#fef2f2", borderBottom: "1px solid #fecaca", fontFamily: MODAL_FONT, fontSize: "12px", color: "#b91c1c" }}>
                {errorGiro}
              </div>
            )}

            <div
              ref={viewerRef}
              style={{ position: "relative", height: `${VIEWER_HEIGHT}px`, background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", cursor: isImage && zoomLevel > 1 ? (dragging ? "grabbing" : "grab") : "default", userSelect: "none" }}
              onMouseDown={(e) => {
                if (!isImage || zoomLevel <= 1) return;
                isDragging.current = true;
                setDragging(true);
                dragStart.current = { x: e.clientX, y: e.clientY, ox: imgOffset.x, oy: imgOffset.y };
                e.preventDefault();
              }}
              onMouseMove={(e) => {
                if (!isDragging.current) return;
                setImgOffset({ x: dragStart.current.ox + (e.clientX - dragStart.current.x), y: dragStart.current.oy + (e.clientY - dragStart.current.y) });
              }}
              onMouseUp={() => { isDragging.current = false; setDragging(false); }}
              onMouseLeave={() => { isDragging.current = false; setDragging(false); }}
            >
              <AnimatePresence>
                {(previewLoading || guardandoGiro) && (isImage || isPdf) && (
                  <motion.div
                    key="preview-spinner"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
                    style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", background: "#f8fafc", zIndex: 1, pointerEvents: "none" }}
                  >
                    <CircularProgress size={28} thickness={4} sx={{ color: "#0065DD" }} />
                    <span style={{ fontFamily: MODAL_FONT, fontSize: "12px", color: "#64748b" }}>
                      {guardandoGiro ? "Guardando rotación…" : "Cargando documento…"}
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {isImage ? (
                <img
                  key={version}
                  src={viewerUrl}
                  alt={doc.originalFileName}
                  draggable={false}
                  onLoad={() => setPreviewLoading(false)}
                  onError={() => setPreviewLoading(false)}
                  style={{
                    // Girada de lado, el ancho visual es su alto: se limitan al revés.
                    maxWidth: zoomLevel === 1 ? (deLado ? `${VIEWER_HEIGHT}px` : "100%") : "none",
                    maxHeight: zoomLevel === 1 ? (deLado ? `${anchoVisor}px` : `${VIEWER_HEIGHT}px`) : "none",
                    objectFit: "contain",
                    transform: `translate(${imgOffset.x}px, ${imgOffset.y}px) rotate(${giroVista}deg) scale(${zoomLevel})`,
                    transformOrigin: "center center",
                    transition: dragging ? "none" : "transform 0.2s ease",
                    pointerEvents: "none",
                    opacity: previewLoading ? 0 : 1,
                  }}
                />
              ) : isPdf ? (
                <iframe
                  key={`${pdfPage}-${version}`}
                  src={pdfSrc}
                  title={doc.originalFileName}
                  onLoad={() => setPreviewLoading(false)}
                  style={{
                    flexShrink: 0,
                    // Girado de lado se intercambian ancho y alto para que, ya
                    // rotado, ocupe exactamente el área del visor.
                    width: deLado ? `${VIEWER_HEIGHT}px` : "100%",
                    height: deLado ? `${anchoVisor}px` : `${VIEWER_HEIGHT}px`,
                    border: "none",
                    transform: `rotate(${giroVista}deg)`,
                    transition: "transform 0.2s ease",
                    opacity: previewLoading ? 0 : 1,
                  }}
                />
              ) : (
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: MODAL_FONT, fontSize: "13px", color: "#94a3b8", marginBottom: "16px" }}>
                    Previsualización no disponible para este formato
                  </div>
                  <a
                    href={`${appApiBase}/application/document-file/${encodeURIComponent(doc.personDocumentId)}`}
                    download={doc.originalFileName}
                    style={{ padding: "8px 20px", borderRadius: "9px", border: "1px solid #bfdbfe", background: "#eff6ff", color: "#0065DD", fontFamily: MODAL_FONT, fontSize: "13px", fontWeight: 500, textDecoration: "none" }}
                  >
                    Descargar archivo
                  </a>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentoConVisor;
